import { FC, useCallback, useEffect, useState } from "react";
import { dispatch } from "../../dispatch/dispatch";
import { actions } from "../../dispatch";
import getDate from "../../getDate";
import { ageAr, ageLevel, countOrders, countPieces, holdReasonAr, newOrdersMsg, sizeAr, t } from "../../i18n/ar";
import { LocationBadge } from "../packing/LocationBadge";
import { StockChip } from "../packing/StockChip";
import { PickSort, PickSortBar, comparePick, loadPickSort, savePickSort } from "../packing/PickSort";
import { playNew } from "../../sound";
import { IErrorResponse } from "../../interfaces/responses/ErrorResponse.interface";
import { SwapPicker, undoSwap } from "../packing/SwapPicker";
import {
    IMarjanOrder,
    IMarjanPackingResponse,
    IMarjanProduct,
    MarjanPrepReason,
    MarjanPrepStatus,
} from "../../interfaces/responses/MarjanPackingResponse.interface";

// ─── Marjan preparation (تجهيز طلبات مرجان) ─────────────────────────────────
// A SEPARATE flow from the Jumia packing view, in Marjane teal. The rule:
// Marjane's handlers cancel an order the moment a piece is missing AFTER the
// seller confirmed + printed it. So the warehouse verifies every piece FIRST
// (this screen), and only orders marked ✅ ready get confirmed — manually, in
// Seller Zone, by the office. Nothing here talks to Marjane.
//   للتحقق  — new "Acceptée" orders: fetch the pieces, then ✅ / ❌
//   جاهزة   — verified, waiting for the office to confirm + print
//   ناقصة   — a piece is missing: do NOT confirm (Telegram already sent)
//   مؤكدة   — already "Préparée" on Marjane: keep the parcel ready for pickup
// The verdict is stored server-side (all devices, the office sees it too).

const SEEN_KEY = "marjan.seen";
const NEW_KEY = "marjan.new";
const TEAL = "#00afaa";

const loadSet = (key: string): Set<string> | null => {
    try {
        const raw = localStorage.getItem(key);
        if (raw) return new Set(JSON.parse(raw));
    } catch {}
    return null;
};
const saveSet = (key: string, value: Iterable<string>) => {
    try {
        localStorage.setItem(key, JSON.stringify(Array.from(value)));
    } catch {}
};

type Tab = "orders" | "products";

interface IZoom {
    imageUrl: string;
    name: string;
    qty?: number;
}

export const MarjanPrep: FC<{ setShowSpinner: (v: boolean) => void }> = ({ setShowSpinner }) => {
    const [data, setData] = useState<IMarjanPackingResponse | null>(null);
    const [error, setError] = useState("");
    const [tab, setTab] = useState<Tab>("orders");
    const [newKeys, setNewKeys] = useState<Set<string>>(() => loadSet(NEW_KEY) || new Set());
    const [zoom, setZoom] = useState<IZoom | null>(null);
    const [now, setNow] = useState<number>(Date.now());
    const [busy, setBusy] = useState<string>("");
    const [pickSort, setPickSort] = useState<PickSort>(() => loadPickSort("marjan.sort"));
    const changePickSort = (v: PickSort) => { savePickSort("marjan.sort", v); setPickSort(v); };

    const load = useCallback(
        async (quiet = false) => {
            if (!quiet) setShowSpinner(true);
            const resp: IMarjanPackingResponse | IErrorResponse = await dispatch(actions.getMarjanPacking);
            if (!quiet) setShowSpinner(false);
            if (!resp || "error" in resp) {
                setError(t.marjanLoadError);
                return;
            }
            setError("");
            setData(resp);
            setNow(Date.now());
            // 🆕 detection — orders absent at the previous refresh chime once and
            // keep the badge until they leave the "to check" stage.
            const checkKeys = resp.orders.filter((o) => o.stage === "accepted" && o.prep.status === "pending").map((o) => o.key);
            const allKeys = resp.orders.map((o) => o.key);
            const prevSeen = loadSet(SEEN_KEY);
            setNewKeys((prev) => {
                const next = new Set(Array.from(prev).filter((k) => checkKeys.includes(k)));
                let arrivals = 0;
                if (prevSeen) {
                    for (const k of checkKeys) {
                        if (!prevSeen.has(k) && !next.has(k)) {
                            next.add(k);
                            arrivals++;
                        }
                    }
                }
                if (arrivals > 0) playNew(newOrdersMsg(arrivals));
                saveSet(NEW_KEY, next);
                return next;
            });
            saveSet(SEEN_KEY, allKeys);
        },
        [setShowSpinner]
    );

    useEffect(() => {
        load();
        // The server polls Marjane every minute — refresh at the same pace.
        const id = setInterval(() => load(true), 60 * 1000);
        return () => clearInterval(id);
    }, [load]);

    const setPrep = async (o: IMarjanOrder, status: MarjanPrepStatus, reason?: MarjanPrepReason) => {
        setBusy(o.key);
        const resp = await dispatch(actions.marjanPrep, { storeId: o.storeId, orderReference: o.orderReference, status, reason });
        setBusy("");
        if (!resp || "error" in resp) return setError(t.marjanSaveError);
        if (status !== "pending") {
            setNewKeys((prev) => {
                if (!prev.has(o.key)) return prev;
                const next = new Set(prev);
                next.delete(o.key);
                saveSet(NEW_KEY, next);
                return next;
            });
        }
        await load(true);
    };

    const orders = data?.orders || [];
    const byAge = (a: IMarjanOrder, b: IMarjanOrder) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
    // Only orders still waiting for the seller's confirmation carry a verdict.
    const open = (o: IMarjanOrder) => o.stage === "accepted" || o.stage === "other";
    const toCheck = orders.filter((o) => open(o) && o.prep.status === "pending").sort(byAge);
    const ready = orders.filter((o) => open(o) && o.prep.status === "ready").sort(byAge);
    const missing = orders.filter((o) => (open(o) || o.stage === "prepared") && o.prep.status === "missing").sort(byAge);
    const prepared = orders.filter((o) => o.stage === "prepared" && o.prep.status !== "missing").sort(byAge);
    // Printed (CDA générée): documents printed, parcel still with us → hand over.
    // Received (Reçu MM) orders leave the list, like shipped ones on Jumia.
    const printed = orders.filter((o) => o.stage === "printed").sort(byAge);
    const newCount = toCheck.filter((o) => newKeys.has(o.key)).length;
    const readySet = new Set(orders.filter((o) => o.prep.status === "ready" || o.stage === "printed").map((o) => o.key));
    const orderDates = new Map(orders.map((o) => [o.key, o.createdAt]));

    // Pick list: subtract the qty of orders already verified (their pieces are
    // in hand). Missing orders are already excluded server-side.
    const productViews = (data?.products || [])
        .map((p) => {
            let taken = 0;
            let oldestUndone: string | null = null;
            let isNew = false;
            for (const po of p.perOrder || []) {
                if (readySet.has(po.key)) {
                    taken += po.qty;
                    continue;
                }
                if (newKeys.has(po.key)) isNew = true;
                const d = orderDates.get(po.key);
                if (d && (!oldestUndone || new Date(d) < new Date(oldestUndone))) oldestUndone = d;
            }
            return { product: p, taken, left: p.totalQty - taken, oldestUndone, isNew };
        })
        .sort(comparePick(pickSort));

    return (
        <div className="mt-4">
            {/* Title + refresh — teal = Marjan, never orange */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="flex items-center gap-2 text-3xl font-black" style={{ color: TEAL }}>
                    <span aria-hidden>🛒</span> {t.marjanTitle}
                </h1>
                <button
                    onClick={() => load()}
                    className="flex items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-4 py-2 text-xl font-black text-slate-700 shadow-sm transition-colors hover:bg-slate-50 active:bg-slate-100"
                >
                    <span className="text-2xl" aria-hidden>🔄</span> {t.refresh}
                </button>
            </div>

            {/* THE rule — always visible */}
            <div className="mt-3 rounded-2xl border-4 border-amber-400 bg-amber-50 px-4 py-3">
                <p className="text-xl font-black text-amber-900">⚠️ {t.marjanRule}</p>
                <p className="mt-1 text-base font-bold text-amber-800">{t.marjanRuleSub}</p>
            </div>

            {error && (
                <p className="mt-4 rounded-2xl bg-red-100 px-4 py-4 text-center text-2xl font-black text-red-700">⚠️ {error}</p>
            )}

            {data && (
                <>
                    {/* Big counters */}
                    <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5 sm:gap-3">
                        <StatTile icon="🔎" value={toCheck.length} label={t.marjanToCheckTile} tone={toCheck.length > 0 ? "teal" : "plain"} />
                        <StatTile icon="✅" value={ready.length} label={t.marjanReadyTile} tone={ready.length > 0 ? "green" : "plain"} />
                        <StatTile icon="❌" value={missing.length} label={t.marjanMissingTile} tone={missing.length > 0 ? "red" : "plain"} />
                        <StatTile icon="📄" value={prepared.length} label={t.marjanConfirmedTile} tone="plain" />
                        <StatTile icon="🖨️" value={printed.length} label={t.marjanPrintedTile} tone={printed.length > 0 ? "indigo" : "plain"} />
                    </div>

                    {newCount > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-lg font-bold">
                            <span className="animate-pulse rounded-xl bg-sky-500 px-3 py-1.5 text-white shadow">🆕 {newOrdersMsg(newCount)}</span>
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="mt-4 grid grid-cols-2 gap-2">
                        <TabButton active={tab === "orders"} onClick={() => setTab("orders")} icon="🧾" label={t.ordersTab} />
                        <TabButton active={tab === "products"} onClick={() => setTab("products")} icon="🏷️" label={t.productsTab} />
                    </div>

                    {data.totalOrders === 0 && (
                        <p className="mt-8 rounded-3xl bg-emerald-50 px-4 py-10 text-center text-3xl font-black text-emerald-700">{t.marjanEmpty}</p>
                    )}

                    {data.totalOrders > 0 && (
                        <p className="mt-3 text-center text-sm font-bold text-slate-400">
                            {t.tapToZoom} · 📍 {t.locationHint} · {t.stockHint}
                        </p>
                    )}

                    {tab === "orders" && data.totalOrders > 0 && (
                        <div className="mt-3 space-y-8">
                            <Section title={t.marjanSectionCheck} icon="🔎" tone="teal" count={toCheck.length} pieces={toCheck.reduce((s, o) => s + o.totalPieces, 0)} pulse={toCheck.length > 0}>
                                {toCheck.map((o, i) => (
                                    <OrderCard key={o.key} order={o} index={i} isNew={newKeys.has(o.key)} now={now} busy={busy === o.key} onPrep={(s, r) => setPrep(o, s, r)} onZoom={setZoom} onChanged={() => load()} />
                                ))}
                            </Section>
                            <Section title={t.marjanSectionReady} icon="✅" tone="green" count={ready.length} pieces={ready.reduce((s, o) => s + o.totalPieces, 0)}>
                                {ready.map((o, i) => (
                                    <OrderCard key={o.key} order={o} index={i} isNew={false} now={now} busy={busy === o.key} onPrep={(s, r) => setPrep(o, s, r)} onZoom={setZoom} onChanged={() => load()} />
                                ))}
                            </Section>
                            <Section title={t.marjanSectionMissing} icon="❌" tone="red" count={missing.length} pieces={missing.reduce((s, o) => s + o.totalPieces, 0)}>
                                {missing.map((o, i) => (
                                    <OrderCard key={o.key} order={o} index={i} isNew={false} now={now} busy={busy === o.key} onPrep={(s, r) => setPrep(o, s, r)} onZoom={setZoom} onChanged={() => load()} />
                                ))}
                            </Section>
                            <Section title={t.marjanSectionPrepared} icon="📄" tone="plain" count={prepared.length} pieces={prepared.reduce((s, o) => s + o.totalPieces, 0)}>
                                {prepared.map((o, i) => (
                                    <OrderCard key={o.key} order={o} index={i} isNew={false} now={now} busy={busy === o.key} onPrep={(s, r) => setPrep(o, s, r)} onZoom={setZoom} onChanged={() => load()} />
                                ))}
                            </Section>
                            <Section title={t.marjanSectionPrinted} icon="🖨️" tone="indigo" count={printed.length} pieces={printed.reduce((s, o) => s + o.totalPieces, 0)} pulse={printed.length > 0}>
                                {printed.map((o, i) => (
                                    <OrderCard key={o.key} order={o} index={i} isNew={false} now={now} busy={false} onPrep={() => {}} onZoom={setZoom} />
                                ))}
                            </Section>
                        </div>
                    )}

                    {tab === "products" && data.totalOrders > 0 && (
                        <>
                            <p className="mt-3 text-center text-lg font-black text-slate-600">🏷️ {t.marjanPickHint}</p>
                            <PickSortBar value={pickSort} onChange={changePickSort} accent={TEAL} />
                            <section className="mt-3 grid gap-4 md:grid-cols-2">
                                {productViews.map((v) => (
                                    <ProductCard key={v.product.key} product={v.product} left={v.left} taken={v.taken} isNew={v.isNew} oldestUndone={v.oldestUndone} now={now} onZoom={setZoom} />
                                ))}
                            </section>
                        </>
                    )}

                    <p className="mt-6 text-center text-sm font-bold text-slate-400">
                        {t.marjanLastUpdate}: <span className="ltr">{getDate(new Date(data.generatedAt), "HH:mm")}</span>
                    </p>
                </>
            )}

            {zoom && (
                <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 p-3" onClick={() => setZoom(null)}>
                    <img src={zoom.imageUrl} alt="" className="max-h-[80vh] max-w-[96vw] rounded-2xl bg-white object-contain" />
                    <div className="mt-3 flex items-center gap-4">
                        {zoom.qty != null && <span className="ltr rounded-2xl bg-amber-400 px-4 py-1 text-5xl font-black text-slate-900">×{zoom.qty}</span>}
                        <p className="ltr max-w-xl text-center text-xl font-bold text-white">{zoom.name}</p>
                    </div>
                    <button className="mt-4 rounded-2xl bg-white px-8 py-3 text-2xl font-black text-slate-900">✕</button>
                </div>
            )}
        </div>
    );
};

type Tone = "teal" | "green" | "red" | "plain" | "indigo";
const toneCls: Record<Tone, string> = {
    teal: "border-[#00afaa] bg-[#e6f7f6]",
    green: "border-emerald-500 bg-emerald-50",
    red: "border-red-500 bg-red-50",
    plain: "border-slate-200 bg-white",
    indigo: "border-indigo-500 bg-indigo-50",
};
const toneText: Record<Tone, string> = {
    teal: "text-[#00847f]",
    green: "text-emerald-700",
    red: "text-red-700",
    plain: "text-slate-600",
    indigo: "text-indigo-700",
};

const StatTile: FC<{ icon: string; value: number; label: string; tone: Tone }> = ({ icon, value, label, tone }) => (
    <div className={`flex flex-col items-center rounded-2xl border-2 px-2 py-3 shadow-sm ${toneCls[tone]} ${tone === "red" ? "animate-pulse" : ""}`}>
        <span className="text-3xl" aria-hidden>{icon}</span>
        <span className={`ltr text-4xl font-black sm:text-5xl ${tone === "plain" ? "text-slate-900" : toneText[tone]}`}>{value}</span>
        <span className={`text-base font-bold ${toneText[tone]}`}>{label}</span>
    </div>
);

const TabButton: FC<{ active: boolean; onClick: () => void; icon: string; label: string }> = ({ active, onClick, icon, label }) => (
    <button
        onClick={onClick}
        className={`flex items-center justify-center gap-2 rounded-2xl border-4 px-4 py-3 text-2xl font-black transition-colors ${
            active ? "border-[#00afaa] bg-[#00afaa] text-white shadow-lg" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
        }`}
    >
        <span aria-hidden>{icon}</span> {label}
    </button>
);

// A titled block of orders; hidden when empty so the worker only sees what matters.
const Section: FC<{ title: string; icon: string; tone: Tone; count: number; pieces: number; pulse?: boolean; children?: React.ReactNode }> = ({ title, icon, tone, count, pieces, pulse, children }) => {
    if (count === 0) return null;
    const head: Record<Tone, string> = {
        teal: "bg-[#00afaa] text-white",
        green: "bg-emerald-600 text-white",
        red: "bg-red-600 text-white",
        plain: "bg-slate-200 text-slate-800",
        indigo: "bg-indigo-600 text-white",
    };
    return (
        <div className={`rounded-3xl border-4 p-3 sm:p-4 ${toneCls[tone]}`}>
            <h2 className={`flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3 text-2xl font-black ${head[tone]}`}>
                <span className={`text-3xl ${pulse ? "animate-pulse" : ""}`} aria-hidden>{icon}</span>
                <span>{title}</span>
                <span className="ms-auto rounded-xl bg-black/15 px-3 py-1 text-lg font-black">{countOrders(count)} · {countPieces(pieces)}</span>
            </h2>
            <div className="mt-3 space-y-4">{children}</div>
        </div>
    );
};

const AgeChip: FC<{ date: string; now: number; subtle?: boolean }> = ({ date, now, subtle }) => {
    const level = ageLevel(date, now);
    const cls =
        level === "critical" ? "animate-pulse bg-red-600 text-white" : level === "old" ? "bg-amber-400 text-slate-900" : subtle ? "bg-black/20 text-white" : "bg-slate-100 text-slate-600";
    return (
        <span className={`rounded-lg px-2.5 py-1 text-base font-black ${cls}`}>
            🕐 {ageAr(date, now)}
            {level === "critical" && ` — ${t.veryOldBadge}`}
            {level === "old" && ` — ${t.oldBadge}`}
        </span>
    );
};

const NewChip: FC = () => <span className="animate-pulse rounded-lg bg-sky-500 px-2.5 py-1 text-base font-black text-white">🆕 {t.newBadge}</span>;

// ── Pick list card (same reading as the Jumia one: giant REMAINING qty + where) ──
const ProductCard: FC<{ product: IMarjanProduct; left: number; taken: number; isNew: boolean; oldestUndone: string | null; now: number; onZoom: (z: IZoom) => void }> = ({
    product, left, taken, isNew, oldestUndone, now, onZoom,
}) => (
    <div className={`overflow-hidden rounded-3xl border-2 bg-white shadow-lg ${left === 0 ? "border-emerald-300 opacity-60" : isNew ? "border-sky-400" : "border-slate-200"}`}>
        {/* phone: picture + qty on top, everything else full width under it (user 2026-10-24); sm+: side by side */}
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:gap-4">
            <button onClick={() => onZoom({ imageUrl: product.imageUrl, name: product.name, qty: left || product.totalQty })} className="mx-auto shrink-0 self-start sm:mx-0" title={product.name}>
                <img src={product.imageUrl} alt="" className="h-40 w-40 rounded-2xl border border-slate-200 bg-white object-contain sm:h-56 sm:w-56" />
            </button>
            <div className="flex min-w-0 flex-1 flex-col gap-3">
                {(isNew || (left > 0 && oldestUndone)) && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {isNew && <NewChip />}
                        {left > 0 && oldestUndone && ageLevel(oldestUndone, now) !== "fresh" && <AgeChip date={oldestUndone} now={now} />}
                    </div>
                )}
                {left > 0 ? (
                    <div className={`flex flex-wrap items-center gap-3 rounded-2xl px-4 py-2 ${left > 1 ? "bg-amber-100 ring-4 ring-amber-500" : "bg-[#e6f7f6]"}`}>
                        <span className="text-xl font-black text-slate-600">{t.take}</span>
                        <span className="ltr text-6xl font-black leading-none text-slate-900">×{left}</span>
                        {taken > 0 && (
                            <span className="ms-auto rounded-lg bg-emerald-100 px-2 py-0.5 text-base font-black text-emerald-700">
                                ✅ <span className="ltr">{taken}</span> {t.takenSoFar}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="rounded-2xl bg-emerald-100 px-4 py-3 text-2xl font-black text-emerald-700">✅ {t.allTaken}</div>
                )}
                <LocationBadge location={product.location} zone={product.zone} overflow={product.overflowLocations} />
                <div><StockChip stock={product.stock} needed={product.totalQty} big /></div>
                {(product.sizeClass === "XL" || product.sizeClass === "XXL" || product.packingNote) && (
                    <div className="flex flex-wrap gap-1.5 text-base font-bold">
                        {(product.sizeClass === "XL" || product.sizeClass === "XXL") && <span className="rounded-lg bg-amber-100 px-2 py-0.5 text-amber-800">📐 {sizeAr(product.sizeClass)}</span>}
                        {product.packingNote && <span className="ltr rounded-lg bg-amber-50 px-2 py-0.5 text-amber-800">📝 {product.packingNote}</span>}
                    </div>
                )}
                {product.groupName && <p className="ltr inline-block max-w-full break-words rounded-lg bg-indigo-50 px-2.5 py-1 text-start text-lg font-black uppercase tracking-wide text-indigo-900">📦 {product.groupName}</p>}
                <p className="ltr text-start text-lg font-bold leading-snug text-slate-700">{product.name}</p>
                <div className="flex flex-wrap gap-1.5">
                    {product.liveUrl && <a href={product.liveUrl} target="_blank" rel="noreferrer" className="ltr inline-flex items-center gap-1 rounded-lg border-2 border-sky-300 bg-sky-50 px-2 py-0.5 text-base font-black text-sky-800">🔗 Marjane Mall</a>}
                    {product.skus.map((sku) => (
                        <span key={sku} className="ltr max-w-full break-all rounded-lg border-2 border-slate-300 bg-slate-50 px-2 py-0.5 font-mono text-xl font-black text-slate-900">{sku}</span>
                    ))}
                </div>
                <div className="mt-auto flex flex-wrap items-center gap-1.5 text-base font-bold">
                    <span className="rounded-lg bg-sky-100 px-2 py-0.5 text-sky-700">🧾 {countOrders(product.ordersCount)}</span>
                    {product.stores.map((s) => (
                        <span key={s} className="ltr rounded-lg bg-[#e6f7f6] px-2 py-0.5 text-[#00847f]">🛒 {s}</span>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

// where a piece is: shelf location, or why there is none
const PieceLocation: FC<{ it: IMarjanOrder["items"][number] }> = ({ it }) =>
    it.location ? (
        <span className="inline-flex max-w-full items-center gap-2 rounded-xl bg-slate-900 px-3 py-1 text-2xl font-black text-white">
            📍 <span className="text-base text-amber-300">{t.locationWord}</span> <span className="ltr break-all font-mono tracking-widest">{it.location}</span>
        </span>
    ) : it.linked ? (
        <span className="inline-block rounded-xl bg-red-100 px-2 py-1 text-base font-black text-red-700">📍 {t.noLocation}</span>
    ) : (
        <span className="inline-block rounded-xl bg-amber-100 px-2 py-1 text-base font-black text-amber-800">🔗 {t.marjanNotLinked}</span>
    );

// ── Order card: pieces to check + the two big verdict buttons ──
const OrderCard: FC<{
    order: IMarjanOrder;
    index: number;
    isNew: boolean;
    now: number;
    busy: boolean;
    onPrep: (status: MarjanPrepStatus, reason?: MarjanPrepReason) => void;
    onZoom: (z: IZoom) => void;
    onChanged?: () => void;
}> = ({ order, index, isNew, now, busy, onPrep, onZoom, onChanged }) => {
    const [pickReason, setPickReason] = useState(false);
    const [swapFor, setSwapFor] = useState<IMarjanOrder["items"][number] | null>(null);   // 🔁 picking the product sent instead
    // Misclick guard: every piece must be ticked "✓ في يدي" before the green
    // button works, and the button itself asks once more. Ticks are per card
    // and reset when the card re-renders after a refresh — intentional.
    const [checked, setChecked] = useState<Set<number>>(() => new Set());
    const [confirmReady, setConfirmReady] = useState(false);
    const prep = order.prep.status;
    const allChecked = order.items.length > 0 && order.items.every((_, i) => checked.has(i));
    const toggleChecked = (i: number) =>
        setChecked((prev) => {
            const next = new Set(prev);
            if (next.has(i)) next.delete(i);
            else next.add(i);
            return next;
        });
    const confirmed = order.stage === "prepared";
    const printed = order.stage === "printed";
    const received = order.stage === "received";
    const handedOff = printed || received; // no verdict buttons any more
    const multi = order.items.length > 1;
    const reasons: Array<{ key: MarjanPrepReason; icon: string; label: string }> = [
        { key: "no_stock", icon: "🏬🚫", label: t.reasonNoStock },
        { key: "damaged", icon: "📦💥", label: t.reasonDamaged },
        { key: "not_found", icon: "🔍❓", label: t.reasonNotFound },
        { key: "other", icon: "❔", label: t.reasonOther },
    ];
    const border = received ? "border-emerald-400" : printed ? "border-indigo-500" : prep === "missing" ? "border-red-500" : prep === "ready" ? "border-emerald-500" : confirmed ? "border-slate-300" : multi ? "border-amber-400" : "border-[#00afaa]";
    const headBg = received ? "bg-emerald-700" : printed ? "bg-indigo-600" : prep === "missing" ? "bg-red-600" : prep === "ready" ? "bg-emerald-600" : confirmed ? "bg-slate-600" : "bg-[#00847f]";
    return (
        <div className={`overflow-hidden rounded-3xl border-4 bg-white shadow-lg ${border} ${prep === "ready" && !confirmed ? "opacity-80" : ""}`}>
            {/* Header: reference · city · age · Marjane status */}
            <div className={`flex flex-wrap items-center gap-3 px-4 py-3 text-white ${headBg}`}>
                <span className="ltr flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-xl font-black text-slate-900">{index + 1}</span>
                <div className="min-w-0">
                    <p className="ltr text-start font-mono text-2xl font-black tracking-wider sm:text-3xl">{order.orderReference}</p>
                    {/* store · time · city as separate chips: mixed Arabic/Latin on one line ran together ("16:11SELVIO") */}
                    <div className="mt-1 flex flex-wrap gap-1.5 text-sm font-bold text-white/90">
                        <span className="rounded-md bg-black/15 px-2 py-0.5">🛒 <span className="ltr inline-block">{order.storeName}</span></span>
                        {order.createdAt ? <span className="rounded-md bg-black/15 px-2 py-0.5">🕐 <span className="ltr inline-block">{getDate(new Date(order.createdAt), "DD/MM HH:mm")}</span></span> : null}
                        {order.city ? <span className="rounded-md bg-black/15 px-2 py-0.5">📍 <span className="ltr inline-block">{order.city}</span></span> : null}
                    </div>
                </div>
                {isNew && <NewChip />}
                {prep === "pending" && !handedOff && order.createdAt && <AgeChip date={order.createdAt} now={now} subtle />}
                <span className="rounded-lg bg-black/20 px-2.5 py-1 text-base font-black">{t.marjanStatusWord}: <span className="ltr">{order.marjanStatusRaw || order.marjanStatus}</span></span>
                <span className="ms-auto rounded-lg bg-black/20 px-3 py-1 text-lg font-black">{countPieces(order.totalPieces)}</span>
            </div>

            {/* State line */}
            {prep === "ready" && !handedOff && (
                <div className="flex flex-wrap items-center gap-2 bg-emerald-100 px-4 py-3 text-xl font-black text-emerald-800">
                    <span>✅ {t.marjanReadyHint}</span>
                    {order.prep.updatedAt && <span className="rounded-lg bg-white/70 px-2 py-0.5 text-base">🕐 {ageAr(order.prep.updatedAt, now)}</span>}
                </div>
            )}
            {prep === "missing" && !handedOff && (
                <div className="flex flex-wrap items-center gap-2 bg-red-100 px-4 py-3 text-xl font-black text-red-800">
                    <span>❌ {holdReasonAr(order.prep.reason)} — {t.marjanMissingHint}</span>
                    {order.prep.updatedAt && <span className="rounded-lg bg-white/70 px-2 py-0.5 text-base">🕐 {ageAr(order.prep.updatedAt, now)}</span>}
                </div>
            )}
            {confirmed && !handedOff && prep !== "missing" && (
                <div className="bg-slate-100 px-4 py-2 text-lg font-black text-slate-700">📄 {t.marjanPreparedHint}</div>
            )}
            {printed && (
                <div className="flex flex-wrap items-center gap-2 bg-indigo-100 px-4 py-3 text-xl font-black text-indigo-900">
                    <span>🖨️ {t.marjanPrintedHint}</span>
                    {order.printedAt && <span className="rounded-lg bg-white/70 px-2 py-0.5 text-base">{t.marjanPrintedSince} {ageAr(order.printedAt, now)}</span>}
                </div>
            )}
            {received && (
                <div className="flex flex-wrap items-center gap-2 bg-emerald-100 px-4 py-3 text-xl font-black text-emerald-800">
                    <span>✅ {t.marjanReceivedHint}</span>
                    {order.receivedAt && <span className="rounded-lg bg-white/70 px-2 py-0.5 text-base">{t.marjanReceivedSince} {ageAr(order.receivedAt, now)}</span>}
                </div>
            )}
            {/* No "deliver by" date on purpose: the worker must prepare and hand
                over immediately, not wait for the date Marjane promised the
                customer. Urgency is carried by the age chips only. */}

            {/* Reason picker */}
            {pickReason && (
                <div className="border-b-4 border-red-400 bg-red-50 p-3">
                    <p className="text-xl font-black text-red-900">❌ {t.marjanMissingTitle}</p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                        {reasons.map((r) => (
                            <button
                                key={r.key}
                                onClick={() => { setPickReason(false); onPrep("missing", r.key); }}
                                className="rounded-2xl border-2 border-red-300 bg-white px-3 py-4 text-xl font-black text-slate-800 shadow-sm hover:bg-red-100"
                            >
                                <span className="me-2" aria-hidden>{r.icon}</span>{r.label}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => setPickReason(false)} className="mt-2 w-full rounded-xl px-3 py-2 text-lg font-bold text-slate-500 hover:bg-red-100">↩️ {t.holdCancel}</button>
                </div>
            )}

            {/* Pieces */}
            <div className="divide-y divide-slate-100">
                {order.items.map((it, i) => (
                    // phone (user 2026-10-24): picture + qty + location on top, details full width, buttons at the bottom —
                    // three columns side by side made a ~5,000 px card with one word per line; sm+ keeps one row
                    <div key={`${it.sku || it.gtin}-${i}`} className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-4 sm:p-4">
                        <div className="flex items-center gap-3 sm:contents">
                            <button onClick={() => onZoom({ imageUrl: it.imageUrl, name: it.name, qty: it.qty })} className="shrink-0" title={it.name}>
                                <img src={it.imageUrl} alt="" className="h-32 w-32 rounded-xl border border-slate-200 bg-white object-contain sm:h-36 sm:w-36" />
                            </button>
                            <div className="flex min-w-0 flex-1 flex-col items-start gap-2 sm:hidden">
                                <span className={`ltr inline-block rounded-xl px-3 py-1 text-4xl font-black text-slate-900 ${it.qty > 1 ? "bg-amber-200 ring-2 ring-amber-500" : "bg-slate-100"}`}>×{it.qty}</span>
                                <PieceLocation it={it} />
                            </div>
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="hidden sm:block">
                                <span className={`ltr inline-block rounded-xl px-3 py-1 text-4xl font-black text-slate-900 ${it.qty > 1 ? "bg-amber-200 ring-2 ring-amber-500" : "bg-slate-100"}`}>×{it.qty}</span>
                                <span className="ms-2"><PieceLocation it={it} /></span>
                            </div>
                            {it.groupName && <p className="ltr mt-1.5 inline-block max-w-full break-words rounded-lg bg-indigo-50 px-2.5 py-1 text-start text-base font-black uppercase tracking-wide text-indigo-900">📦 {it.groupName}</p>}
                            <p className="ltr mt-1.5 text-start text-base font-bold leading-snug text-slate-700">{it.name}</p>
                            <p className="ltr break-all text-start font-mono text-lg font-black text-slate-500">{it.sku || it.productId}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                {it.linked && <StockChip stock={it.stock} needed={it.qty} />}
                                {it.liveUrl && <a href={it.liveUrl} target="_blank" rel="noreferrer" className="ltr inline-flex items-center gap-1 rounded-lg border-2 border-sky-300 bg-sky-50 px-2 py-0.5 text-base font-black text-sky-800">🔗 Marjane Mall</a>}
                            </div>
                            {/* 🔁 sent another product for this line (not found on the shelf) */}
                            {it.swap && (
                                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-2xl border-2 border-violet-400 bg-violet-50 p-2">
                                    {it.swap.image ? <img src={it.swap.image} alt="" className="h-14 w-14 rounded-lg bg-white object-contain" /> : null}
                                    <div className="min-w-0 flex-1"><p className="text-base font-black text-violet-900">🔁 صيفطتي بلاصتو:</p><p className="ltr break-words text-start text-lg font-black text-slate-900">{it.swap.name}</p></div>
                                    {!["printed", "received"].includes(order.stage) && <button onClick={async () => { await undoSwap({ platform: "marjan", storeId: order.storeId, orderId: order.orderReference, sku: it.sku }); onChanged?.(); }} className="rounded-xl bg-white px-3 py-2 text-base font-black text-slate-600 ring-1 ring-slate-300">↩️ إلغاء</button>}
                                </div>
                            )}
                        </div>
                        {(prep === "pending" || (!it.swap && !["printed", "received"].includes(order.stage) && it.sku)) && (
                            <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-col">
                                {!it.swap && !["printed", "received"].includes(order.stage) && it.sku ? (
                                    <button onClick={() => setSwapFor(it)} className="rounded-2xl border-2 border-violet-300 bg-white px-2 py-2 text-base font-black leading-tight text-violet-800 hover:bg-violet-50 sm:order-2">🔁 ما لقيتوش — صيفطت منتج آخر</button>
                                ) : <span />}
                                {prep === "pending" && (
                                    <button
                                        onClick={() => toggleChecked(i)}
                                        className={`flex min-h-[5rem] flex-col items-center justify-center rounded-2xl border-4 text-lg font-black shadow transition-colors sm:order-1 sm:h-28 sm:w-28 ${
                                            checked.has(i) ? "border-emerald-600 bg-emerald-500 text-white" : "border-slate-300 bg-white text-slate-400 hover:bg-slate-50"
                                        }`}
                                    >
                                        <span className="text-4xl" aria-hidden>{checked.has(i) ? "✅" : "☐"}</span>
                                        <span>{checked.has(i) ? t.marjanPieceChecked : t.marjanPieceCheck}</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {swapFor && (
                <SwapPicker platform="marjan" storeId={order.storeId} orderId={order.orderReference} orderNumber={order.orderReference} sku={swapFor.sku} qty={swapFor.qty}
                    orderedName={swapFor.groupName || swapFor.name} orderedImage={swapFor.imageUrl}
                    onClose={() => setSwapFor(null)} onDone={() => { setSwapFor(null); onChanged?.(); }} />
            )}

            {/* Verdict buttons — huge, two choices (none once printed / received) */}
            {!handedOff && <div className="grid gap-2 border-t-4 border-slate-100 p-3 sm:grid-cols-2">
                {prep === "pending" && !confirmReady && (
                    <>
                        <button
                            disabled={busy || !allChecked}
                            onClick={() => setConfirmReady(true)}
                            className={`rounded-2xl px-4 py-4 text-2xl font-black shadow transition-colors ${
                                allChecked ? "bg-emerald-500 text-white hover:bg-emerald-400" : "cursor-not-allowed bg-slate-200 text-slate-400"
                            } disabled:opacity-60`}
                        >
                            {allChecked ? t.marjanAllPresentBtn : `☐ ${t.marjanTickFirst} (${checked.size}/${order.items.length})`}
                        </button>
                        <button
                            disabled={busy}
                            onClick={() => setPickReason((v) => !v)}
                            className={`rounded-2xl border-4 px-4 py-4 text-2xl font-black shadow transition-colors disabled:opacity-50 ${pickReason ? "border-red-600 bg-red-600 text-white" : "border-red-500 bg-white text-red-600 hover:bg-red-50"}`}
                        >
                            {t.marjanMissingBtn}
                        </button>
                    </>
                )}
                {prep === "pending" && confirmReady && (
                    <div className="rounded-2xl border-4 border-emerald-500 bg-emerald-50 p-3 sm:col-span-2">
                        <p className="text-center text-2xl font-black text-emerald-900">❓ {t.marjanConfirmQuestion}</p>
                        <div className="mt-3 grid grid-cols-2 gap-2">
                            <button
                                disabled={busy}
                                onClick={() => { setConfirmReady(false); onPrep("ready"); }}
                                className="rounded-2xl bg-emerald-600 px-4 py-4 text-2xl font-black text-white shadow hover:bg-emerald-500 disabled:opacity-50"
                            >
                                {t.marjanConfirmYes}
                            </button>
                            <button
                                onClick={() => setConfirmReady(false)}
                                className="rounded-2xl border-4 border-slate-300 bg-white px-4 py-4 text-2xl font-black text-slate-700 hover:bg-slate-50"
                            >
                                {t.marjanConfirmNo}
                            </button>
                        </div>
                    </div>
                )}
                {prep === "ready" && (
                    <button disabled={busy} onClick={() => onPrep("pending")} className="rounded-2xl border-2 border-slate-300 bg-white px-4 py-3 text-xl font-black text-slate-600 hover:bg-slate-50 disabled:opacity-50 sm:col-span-2">
                        ↩️ {t.marjanUndoBtn}
                    </button>
                )}
                {prep === "missing" && (
                    <button disabled={busy} onClick={() => onPrep("pending")} className="rounded-2xl bg-white px-4 py-4 text-2xl font-black text-emerald-700 ring-4 ring-emerald-400 hover:bg-emerald-50 disabled:opacity-50 sm:col-span-2">
                        ▶️ {t.marjanRecheckBtn}
                    </button>
                )}
            </div>}
        </div>
    );
};
